import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAssignmentHistory } from '../services/firebaseService';
import { assignmentSessionListKeys } from './assignmentChunkKeys';

const SESSION_LIST_STALE_MS = 30 * 1000;

/** Max sessions TimeFilter / history list may request (soft UI + query guard). */
export const SESSION_LIST_MAX_LIMIT = 50;
export const SESSION_LIST_DEFAULT_LIMIT = 10;

export function clampSessionListLimit(limit) {
  const n = parseInt(limit, 10);
  if (Number.isNaN(n) || n < 1) return SESSION_LIST_DEFAULT_LIMIT;
  return Math.min(n, SESSION_LIST_MAX_LIMIT);
}

export function useSessionList(limitCount, options = {}) {
  const limit = clampSessionListLimit(limitCount);
  const enabled = options.enabled ?? true;

  return useQuery({
    queryKey: assignmentSessionListKeys.list(limit),
    queryFn: () => getAssignmentHistory(limit),
    enabled,
    staleTime: SESSION_LIST_STALE_MS,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

export function useInvalidateSessionList() {
  const queryClient = useQueryClient();

  return useCallback(
    (limitCount) => {
      if (limitCount != null) {
        return queryClient.invalidateQueries({
          queryKey: assignmentSessionListKeys.list(clampSessionListLimit(limitCount)),
        });
      }
      return queryClient.invalidateQueries({
        queryKey: assignmentSessionListKeys.all,
      });
    },
    [queryClient]
  );
}

export function useRefreshSessionList() {
  const queryClient = useQueryClient();

  return useCallback(
    async (limitCount) => {
      const limit = clampSessionListLimit(limitCount);
      await queryClient.invalidateQueries({
        queryKey: assignmentSessionListKeys.list(limit),
      });
      await queryClient.refetchQueries({
        queryKey: assignmentSessionListKeys.list(limit),
      });
    },
    [queryClient]
  );
}
